<template>
	<div class="inner-page add-user">
		<AppHead title="Create new user" />

		<PageHeader 
			title="Add User"
			desc="Add a new user to the Document Room"
			:hasButton="false"
			:showHelper="true"
			helpTitle="Adding a new user"
			helpDesc="Fill out the form and assign a user level to add a new user to the Document Room. Admin users will have access to ALL Cases. Regular users only have access to their own Cases."
			:hr="true"
			/>

		<InputGroup
			title="New User Details"
			desc="Please fill in all fields to create a new user."
			>

			<ValidationErrors />
			
			<InputText
				label="First Name"
				type="text"
				v-model="form.firstName"
				autocomplete="disabled"
				/>
			<InputText
				label="Surname"
				type="text"
				v-model="form.surname"
				autocomplete="disabled"
				/>
			<InputText
				label="Email"
				type="email"
				v-model="form.email"
				autocomplete="disabled"
				/>

		</InputGroup>

		<InputGroup
			title="User Type"
			desc="Please select either Admin or User. Please note: Admins can view ALL Cases. Assign with caution."
			>
			<InputFancyRadio 
				label="Select the level of authorisation"
				:options="authLevels"
				id="privilegelevel"
				v-model="form.user_type"
				/>
		</InputGroup>

		<InputGroup
			title="Notification Preferences"
			desc="How should the user be contacted about updates to cases they are assigned to?"
			>
			<InputFancyRadio 
				label="Notification Preferences"
				:options="contactPrefOptions"
				id="contactPref"
				v-model="form.contact_pref"
				/>

			<InputFancyRadio 
				v-if="form.user_type === 'admin'"
				label="Get updates for all cases?"
				:options="getUpdatesForAllCasesOptions"
				id="getUpdatesForAllCases"
				v-model="form.getUpdatesForAllCases"
				/>
		</InputGroup>

	
		<InputGroup
			title="Tag"
			desc="Now assign a tag to the user so they will appear in the correct lists and be assignable to cases."
			>
			<InputFancyRadio 
				label="Select the tag to assign to the user"
				:options="userTags"
				id="userTag"
				v-model="form.tag"
				/>
		</InputGroup>

		<InputGroup
			title="Hide user"
			desc="You can choose to hide this user from user selection lists when assigning users to cases. For example, if this is a test user."
			>
			<InputFancyRadio 
				label="Select if the user should be hidden"
				:options="hideFromListsOptions"
				id="hideFromListsOptions"
				v-model="form.hideFromLists"
				/>
		</InputGroup>


		<InputGroup
			title=""
			desc=""
			:skipWhiteBox="true"
			:noLine="true"
			:buttonBox="true"
			>
			<Button
				label="Create"
				color="green"
				size="regular"
				icon="plus" 
				@click="submit"
				/>

			<Button
				label="Cancel"
				color="white"
				size="regular"
				icon="chevron-left" 
				@click="cancel"
				/>
		</InputGroup>

	</div>
</template>

<script>
import AppHead from "@/Components/AppHead"
import AppLayout from "@/Layouts/AppLayout.vue"
import PageHeader from "@/Components/PageHeader"
import InputGroup from "@/Components/InputGroup"
import InputText from "@/Components/InputText"
import InputFancyRadio from "@/Components/InputFancyRadio"
import Button from "@/Components/Button"
import ValidationErrors from "@/Components/ValidationErrors"

export default {
	components: {
		AppHead,
		PageHeader,
		InputGroup,
		InputText,
		InputFancyRadio,
		ValidationErrors,
		Button
	},
	layout: AppLayout,
	data: () => {
		return {
			authLevels: [
				{
					value: 'admin',
					text: 'Super Admin',
					desc: 'Can view and edit any of the cases. Can also change all other settings.'
				},
				{
					value: 'user',
					text: 'User',
					desc: 'Can view and edit cases they are attached to as a Surgeon, Rep or Viewer, but they cannot view other cases by default.'
				}
			],
			contactPrefOptions: [
				{
					value: 'email',
					text: 'Email'
				},
				{
					value: false,
					text: 'Do not notify',
					desc: 'Selecting this option will mean the user will not be updated about cases and will need to login to check progress'
				}
			],
			hideFromListsOptions: [
				{
					value: false,
					text: 'Show in user selection lists'
				},
				{
					value: true,
					text: 'Hide from user selection lists',
					desc: 'Selecting this option will hide the user from user selection lists when assigning users to cases.'
				}
			],
			getUpdatesForAllCasesOptions: [
				{
					value: false,
					text: 'Only get notifications for cases the user has been attached to.'
				},
				{
					value: true,
					text: 'Get notifications for ALL cases',
					desc: 'Always get notifications relevant to admins for EVERY case, regardless of if this admin has been attached to a case. This overrides any individual case notification settings.'
				}
			],
			form: {
				firstName: null,
				surname: null,
				// name: null,
				email: null,
				user_type: 'user',
				tag: null,
				contact_pref: false,
				hideFromLists: false,
				getUpdatesForAllCases: false
			}
		}
	},
	methods: {
		submit(){
			
			const requiredFields = ['firstName', 'surname', 'email', 'user_type']
			const missingFields = requiredFields.filter(field => 
				!this.form[field] || this.form[field] === null || this.form[field] === ''
			)
			
			if(missingFields.length > 0){
				alert(`Please complete all required fields`)
				return
			}

			this.$inertia.post('/admin/add-user', {
				firstName: this.form.firstName,
				lastName: this.form.surname,
				email: this.form.email,
				contact_pref: this.form.contact_pref,
				user_type: this.form.user_type,
				tag: this.form.tag,
				hide_from_lists: this.form.hideFromLists,
				get_updates_for_all_cases: this.form.getUpdatesForAllCases
			})
		},
		cancel(){
			history.back()
		}
	},
	computed: {
		userTags(){
			const userTags = [
				{
					value: null,
					text: 'None',
					desc: 'Users without a tag can only view cases, they cannot edit a case since they can\'t be assigned as a Rep or Surgeon.'
				},
				{
					value: 'rep',
					text: 'Rep',
					desc: 'If assigned as a Rep, they cannot be selected as a Surgeon, they can only be Reps or viewers of cases.'
				},
				{
					value: 'surgeon',
					text: 'Surgeon',
					desc: 'If assigned as a Surgeon they cannot be a Rep of a case, they can only be Surgeons, Supporting Surgeons or viewers of cases'
				}
			]
			if(this.form.user_type == 'admin'){
				// remove value surgeon
				return userTags.filter(tag => tag.value !== 'surgeon')
			}else{
				return userTags
			}
		}
	}
};
</script>

<style lang="sass" scoped>

// .add-user

</style>